import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import AgeEnrolmentExecutiveTab from "@/components/AgeEnrolmentExecutiveTab";
import AgeEnrolmentAgeGenderTab from "@/components/AgeEnrolmentAgeGenderTab";
import AgeEnrolmentGeographyTab from "@/components/AgeEnrolmentGeographyTab";
import AgeEnrolmentPolicyTab from "@/components/AgeEnrolmentPolicyTab";
import AgeEnrolmentInsightsTab from "@/components/AgeEnrolmentInsightsTab";
import "@/components/AgeEnrolmentExecutiveTab.css";
import "@/components/AgeEnrolmentAgeGenderTab.css";
import "@/components/AgeEnrolmentGeographyTab.css";
import "@/components/AgeEnrolmentPolicyTab.css";
import "@/components/AgeEnrolmentInsightsTab.css";
import { 
  Users, 
  RefreshCw,
  Upload,
  School,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Target,
  Scale,
  Baby,
  GraduationCap,
  Building2,
  Brain,
  Lightbulb,
  Map,
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DashboardPageHeader from "@/components/DashboardPageHeader";

import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "executive", label: "Executive Overview", icon: BarChart3 },
  { id: "age-gender", label: "Age & Gender", icon: Users },
  { id: "geography", label: "Geography & Schools", icon: Map },
  { id: "policy", label: "Policy & Management", icon: Building2 },
  { id: "insights", label: "Insights", icon: Lightbulb },
];

// KPI Card Component
const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, subValue }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600",
    pink: "bg-pink-50 text-pink-600",
  };

  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
            {subValue && <p className="text-sm font-medium text-slate-600 mt-1">{subValue}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// GPI Indicator
const GPIIndicator = ({ value, size = "md" }) => {
  const getColor = (gpi) => {
    if (gpi >= 0.97 && gpi <= 1.03) return { bg: "bg-emerald-500", text: "text-emerald-700", label: "Parity" };
    if (gpi >= 0.90) return { bg: "bg-amber-500", text: "text-amber-700", label: "Near Parity" };
    return { bg: "bg-red-500", text: "text-red-700", label: "Gap" };
  };
  
  const { text, label } = getColor(value);
  const sizeClass = size === "lg" ? "text-3xl" : "text-xl";
  
  return (
    <div className="text-center">
      <p className={`font-bold ${sizeClass} ${text}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
};

const AgeEnrolmentDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [ageData, setAgeData] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [managementData, setManagementData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [topSchools, setTopSchools] = useState([]);
  const [schoolSizeData, setSchoolSizeData] = useState(null);
  const [genderByAge, setGenderByAge] = useState([]);
  const [dataQuality, setDataQuality] = useState(null);
  const [activeTab, setActiveTab] = useState("executive");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        ageRes,
        blockRes,
        mgmtRes,
        catRes,
        topRes,
        sizeRes,
        genderRes,
        qualityRes
      ] = await Promise.all([
        axios.get(`${API}/age-enrolment/overview`),
        axios.get(`${API}/age-enrolment/age-wise`),
        axios.get(`${API}/age-enrolment/block-wise`),
        axios.get(`${API}/age-enrolment/management-wise`),
        axios.get(`${API}/age-enrolment/category-wise`),
        axios.get(`${API}/age-enrolment/top-schools`),
        axios.get(`${API}/age-enrolment/school-size-distribution`),
        axios.get(`${API}/age-enrolment/gender-by-age`),
        axios.get(`${API}/age-enrolment/data-quality`)
      ]);
      
      setOverview(overviewRes.data);
      setAgeData(ageRes.data);
      setBlockData(blockRes.data);
      setManagementData(mgmtRes.data);
      setCategoryData(catRes.data);
      setTopSchools(topRes.data);
      setSchoolSizeData(sizeRes.data);
      setGenderByAge(genderRes.data);
      setDataQuality(qualityRes.data);
    } catch (error) {
      console.error("Error fetching Age Enrolment data:", error);
      toast.error("Failed to load age-wise enrolment data");
    } finally {
      setLoading(false);
    }
  }, [
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    setImporting(true);
    try {
      await axios.post(`${API}/age-enrolment/import`);
      toast.success("Age-wise Enrolment import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 15000); // Larger file needs more time
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.total_schools > 0;

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const headerCopy = {
    executive: {
      eyebrow: `Student enrolment by age · ${scopeLabel} · 2025–26`,
      subtitle:
        "Executive-ready view of age distribution, gender parity, early-age participation, retention risk and management mix. The layout reduces KPI overload and brings priority actions into the first screen.",
      icon: BarChart3,
    },
    "age-gender": {
      eyebrow: `Age & Gender · ${scopeLabel} · 2025–26`,
      subtitle:
        "Gender-balanced enrolment monitoring across ages 4–18, redesigned to help district users quickly see parity gaps, retention risk, and the ages needing immediate intervention.",
      icon: Users,
    },
    geography: {
      eyebrow: `Geography & schools · ${scopeLabel} · 2025–26`,
      subtitle:
        "Block and school distribution redesigned for faster scanning: first identify concentration pockets, then review gender parity risk and school-size pressure.",
      icon: Map,
    },
    policy: {
      eyebrow: `Policy & management · ${scopeLabel} · 2025–26`,
      subtitle:
        "Policy-ready view of enrolment by school management, category mix and data quality, designed to help officers identify coverage concentration, equity gaps and validation priorities.",
      icon: Building2,
    },
    insights: {
      eyebrow: `Insights · ${scopeLabel} · 2025–26`,
      subtitle: `Student Enrolment by Age • 2025–26 • ${scopeLabel}`,
      icon: Lightbulb,
    },
  };

  const copy = headerCopy[activeTab] || headerCopy.executive;
  const EyebrowIcon = copy.icon;

  return (
    <div className="aew-dashboard space-y-6 animate-fade-in" data-testid="age-enrolment-dashboard">
      <DashboardPageHeader
        title="Age-wise Enrolment Dashboard"
        subtitle={
          hasData
            ? copy.subtitle
            : "Student Enrolment by Age • 2025-26 • Pune District"
        }
        eyebrow={
          hasData ? (
            <>
              <EyebrowIcon size={14} />
              {copy.eyebrow}
            </>
          ) : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importing}
              data-testid="import-age-enrolment-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <ExportPanel dashboardName="age-enrolment" dashboardTitle="Age-wise Enrolment" />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Age-wise Enrolment Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Age-wise Enrolment Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-age-enrolment-empty-btn">
              {importing ? "Importing..." : "Import Age-wise Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="aew-tabs-wrap" aria-label="Age enrolment dashboard sections">
              <nav className="aew-tabs">
                {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`aew-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="executive" className="mt-0">
              <AgeEnrolmentExecutiveTab
                overview={overview}
                ageData={ageData}
                managementData={managementData}
                blockData={blockData}
              />
            </TabsContent>

            <TabsContent value="age-gender">
              <AgeEnrolmentAgeGenderTab overview={overview} ageData={ageData} blockData={blockData} />
            </TabsContent>

            <TabsContent value="geography">
              <AgeEnrolmentGeographyTab
                overview={overview}
                blockData={blockData}
                schoolSizeData={schoolSizeData}
                topSchools={topSchools}
              />
            </TabsContent>

            <TabsContent value="policy" className="mt-0">
              <AgeEnrolmentPolicyTab
                overview={overview}
                managementData={managementData}
                categoryData={categoryData}
                dataQuality={dataQuality}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <AgeEnrolmentInsightsTab
                overview={overview}
                ageData={ageData}
                blockData={blockData}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default AgeEnrolmentDashboard;
